import React, { useState, useMemo } from "react";
import {
  Box,
  Chip,
  Tooltip,
} from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import moment from "moment";
import { formatTimestampToUserTimezone } from "../../../utils/utils";
import ThunderIcon from "../SvgIcons/ThunderIcon";
import { useNavigate } from 'react-router-dom';
import { CommonDataTable } from '../SharedComponent/CommonDataTable';
import { usePriceFormatter } from "../../../hooks";

const TransactionHistoryTable = ({ transactionHistory }: any) => {
  const navigation = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const formatter = usePriceFormatter()

  const navigateToDetail = async (transactionHistory) => {
    navigation('/boosterLoanDetails', {
      state: {
        symbol: transactionHistory.order.crypto.symbol,
        title: transactionHistory.order.crypto.symbol,
        loanDetails: transactionHistory.order,
        icon: transactionHistory.order.crypto.logo,
        loan_id: transactionHistory.order.boosterLoanId,
      },
    });
  };

  // Define columns for CommonDataTable
  const columns = useMemo(() => [
    {
      name: 'Type',
      center: true,
      selector: (row: any) => row.type || '',
      cell: (row: any) => (
        <Chip
          label={row.type}
          style={{
            fontSize: '14px',
            backgroundColor: row?.type.toLowerCase() === 'buy' ? '#4EDF6D33' : '#FF2A2A33',
            color: row.type.toLowerCase() === 'buy' ? '#4EDF6D' : '#FF2A2A',
            padding: '5px 28px',
          }}
        />
      ),
      sortable: true,
      grow: 1,
      minWidth: '120px',
    },
    {
      name: 'Date',
      center: true,
      selector: (row: any) => row.createdAt || '',
      cell: (row: any) => {
        const { date, time } = formatTimestampToUserTimezone(row.createdAt);

        return (
          <Box sx={{ display: "flex", alignItems: "center", color: "#FFFFFF" }}>
            {row.isPositive ? (
              <ArrowDropUpIcon sx={{ color: "#4CAF50", marginRight: "8px" }} />
            ) : (
              <ArrowDropDownIcon sx={{ color: "#F44336", marginRight: "8px" }} />
            )}
            <span>
              {date}
            </span>
          </Box>
        );
      },
      sortable: true,
      grow: 1,
      minWidth: '150px',
    },
    {
      name: 'Time',
      center: true,
      selector: (row: any) => row.createdAt || '',
      cell: (row: any) => (
        <Box sx={{ color: "#FFFFFF" }}>
          {moment(row.createdAt).format("h:mm A")}
        </Box>
      ),
      sortable: true,
      grow: 1,
      minWidth: '100px',
    },
    {
      name: 'Total Amount',
      center: true,
      selector: (row: any) => row.value * row.Qty || 0,
      cell: (row: any) => (
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ color: "#FFFFFF" }}>
            {formatter.balance(row.value * row.Qty).display}
          </Box>
          {row.loanAmount > 0 && (
            <Tooltip
              placement="top"
              arrow
              title={
                <Box sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '16px',
                      color: 'rgba(255, 255, 255, 0.5)',
                    }}
                  >
                    <ThunderIcon /> Booster Loan Pending Amount
                  </Box>
                  <Box
                    sx={{
                      marginTop: '8px',
                      fontWeight: 800,
                      fontSize: '20px',
                      lineHeight: '27px',
                      color: '#FFFFFF',
                      textAlign: 'center',
                    }}
                  >
                    ${formatter.balance(row.loanAmount).display}
                  </Box>
                  <Box
                    sx={{
                      margin: '8px 0 6px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                      width: '100%',
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      paddingBottom: '10px',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '14px',
                        textDecoration: 'underline',
                        color: '#4A7AF2',
                        cursor: 'pointer',
                        textAlign: 'center',
                      }}
                      onClick={() => { navigateToDetail(row); }}
                    >
                      View details
                    </span>
                  </Box>
                </Box>
              }
            >
              <span style={{ cursor: "pointer", marginLeft: 6 }}>
                <ThunderIcon />
              </span>
            </Tooltip>
          )}
        </Box>
      ),
      sortable: true,
      grow: 1,
      minWidth: '150px',
    },
    {
      name: 'Total Qty.',
      center: true,
      selector: (row: any) => row.Qty || 0,
      cell: (row: any) => (
        <Box sx={{ color: "#FFFFFF" }}>
          {formatter.quantity(row.Qty).display}
        </Box>
      ),
      sortable: true,
      grow: 1,
      minWidth: '120px',
    },
  ], []);

  return (
    <div style={{
      marginTop: '30px',
      color: 'white',
      background: 'transparent',
      borderRadius: '20px',
      border: '2px solid #FFFFFF1A',
      padding: '20px',
      width: '100%',
      overflow: 'hidden'
    }}>
      <CommonDataTable
        data={transactionHistory || []}
        totalRows={transactionHistory?.length || 0}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        columns={columns}
        title="Transaction History"
        pagination={false}
        customStyles={{
          table: {
            style: {
              backgroundColor: 'transparent',
              width: '100%',
            },
          },
          tableWrapper: {
            style: {
              backgroundColor: 'transparent',
              width: '100%',
            },
          },
          headCells: {
            style: {
              fontSize: "12px",
              color: "#BFBFBF",
              fontWeight: "600",
              backgroundColor: "transparent",
              borderBottom: "1px solid #FFFFFF1A",
              paddingBottom: "12px",
              paddingTop: "16px",
              paddingLeft: "8px",
              paddingRight: "8px",
            },
          },
          cells: {
            style: {
              color: "white",
              fontSize: '16px',
              border: "none",
              padding: "12px 4px",
              backgroundColor: "transparent",
            },
          },
          rows: {
            style: {
              borderColor: "transparent !important",
              backgroundColor: 'transparent',
              width: '98% !important',
              borderRadius: "8px",
              margin: '2px 8px',
              padding: "4px",
              transition: "all 0.3s ease",
              cursor: 'pointer',
              outline: "none !important",
              '&:hover': {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: '#4A7AF21A !important',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
                outline: "none !important",
              },
              '&:nth-child(even)': {
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: "1px solid transparent !important",
                borderColor: "transparent !important",
              },
              '&:nth-child(odd)': {
                backgroundColor: 'transparent',
                border: "1px solid transparent !important",
                borderColor: "transparent !important",
              },
              '&:nth-child(even):hover': {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: '#4A7AF21A !important',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
              },
              '&:nth-child(odd):hover': {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: '#4A7AF21A !important',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
              },
            },
          },
        }}
      />
    </div>
  );
};

export default TransactionHistoryTable;